import axios from '../utils/axiosConfig';

const API_BASE = '/api/v1';

// ============================================
// Story Characters API
// ============================================

export const getStoryCharacters = async (projectId) => {
  try {
    const response = await axios.get(`${API_BASE}/story-characters/${projectId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null; // No data found
    }
    throw error;
  }
};

export const saveStoryCharacters = async (projectId, data) => {
  try {
    const response = await axios.post(`${API_BASE}/story-characters/${projectId}`, data);
    return response.data;
  } catch (error) {
    console.error('Error saving story characters:', error);
    throw error;
  }
};

export const deleteStoryCharacters = async (projectId) => {
  try {
    const response = await axios.delete(`${API_BASE}/story-characters/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting story characters:', error);
    throw error;
  }
};

// Upload a character image (portrait or moodboard) to disk storage.
// Returns the public URL of the saved file.
export const uploadCharacterImage = async (projectId, file) => {
  const formData = new FormData();
  formData.append('image', file);
  const response = await axios.post(`${API_BASE}/uploads/characters/${projectId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data.url;
};

// ============================================
// Index Cards API
// ============================================

export const getIndexCards = async (projectId) => {
  try {
    const response = await axios.get(`${API_BASE}/index-cards/${projectId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null; // No data found
    }
    throw error;
  }
};

export const saveIndexCards = async (projectId, data) => {
  try {
    const response = await axios.post(`${API_BASE}/index-cards/${projectId}`, data);
    return response.data;
  } catch (error) {
    console.error('Error saving index cards:', error);
    throw error;
  }
};

export const deleteIndexCards = async (projectId) => {
  try {
    const response = await axios.delete(`${API_BASE}/index-cards/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting index cards:', error);
    throw error;
  }
};

// ============================================
// Mind Map API
// ============================================

export const getMindMap = async (projectId) => {
  try {
    const response = await axios.get(`${API_BASE}/mind-maps/${projectId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null; // No data found
    }
    throw error;
  }
};

export const saveMindMap = async (projectId, data) => {
  try {
    const response = await axios.post(`${API_BASE}/mind-maps/${projectId}`, data);
    return response.data;
  } catch (error) {
    console.error('Error saving mind map:', error);
    throw error;
  }
};

export const deleteMindMap = async (projectId) => {
  try {
    const response = await axios.delete(`${API_BASE}/mind-maps/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting mind map:', error);
    throw error;
  }
};

// ============================================
// World Building API
// ============================================

export const getWorldBuilding = async (projectId) => {
  try {
    const response = await axios.get(`${API_BASE}/world-building/${projectId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null; // No data found
    }
    throw error;
  }
};

export const saveWorldBuilding = async (projectId, data) => {
  try {
    const response = await axios.post(`${API_BASE}/world-building/${projectId}`, data);
    return response.data;
  } catch (error) {
    console.error('Error saving world building:', error);
    throw error;
  }
};

export const deleteWorldBuilding = async (projectId) => {
  try {
    const response = await axios.delete(`${API_BASE}/world-building/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting world building:', error);
    throw error;
  }
};

// ============================================
// Moodboard Metadata API
// ============================================

export const getMoodboardMetadata = async (projectId) => {
  try {
    const response = await axios.get(`${API_BASE}/moodboard-metadata/${projectId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null; // No data found
    }
    throw error;
  }
};

export const saveMoodboardMetadata = async (projectId, data) => {
  try {
    const response = await axios.post(`${API_BASE}/moodboard-metadata/${projectId}`, data);
    return response.data;
  } catch (error) {
    console.error('Error saving moodboard metadata:', error);
    throw error;
  }
};

export const deleteMoodboardMetadata = async (projectId) => {
  try {
    const response = await axios.delete(`${API_BASE}/moodboard-metadata/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting moodboard metadata:', error);
    throw error;
  }
};

// ============================================
// Loglines API
// ============================================

export const getLoglines = async (projectId) => {
  try {
    const response = await axios.get(`${API_BASE}/loglines/${projectId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const saveLoglines = async (projectId, data) => {
  try {
    const response = await axios.post(`${API_BASE}/loglines/${projectId}`, data);
    return response.data;
  } catch (error) {
    console.error('Error saving loglines:', error);
    throw error;
  }
};

export const deleteLoglines = async (projectId) => {
  try {
    const response = await axios.delete(`${API_BASE}/loglines/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting loglines:', error);
    throw error;
  }
};

// ============================================
// Lighting Diagram API
// ============================================

export const getLightingDiagram = async (projectId) => {
  try {
    const response = await axios.get(`${API_BASE}/lighting-diagrams/${projectId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const saveLightingDiagram = async (projectId, data) => {
  try {
    const response = await axios.post(`${API_BASE}/lighting-diagrams/${projectId}`, data);
    return response.data;
  } catch (error) {
    console.error('Error saving lighting diagram:', error);
    throw error;
  }
};

export const deleteLightingDiagram = async (projectId) => {
  try {
    const response = await axios.delete(`${API_BASE}/lighting-diagrams/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting lighting diagram:', error);
    throw error;
  }
};
